import { Link } from "react-router-dom";
import { ArrowRight, Globe, Scale, ClipboardList, Bot } from "lucide-react";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";
const ENGINE = "https://images.unsplash.com/photo-1654616111851-5394318e3279?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";
const VENDORS = ["RockAuto", "AutoZone", "NAPA", "O'Reilly", "eBay Motors", "Amazon", "PartsGeek", "FCP Euro", "Dealer OEM"];

const FEATURES = [
  { icon: Globe, title: "Live web search", text: "The AI checks real retailer pages right now, not a stale catalog." },
  { icon: ClipboardList, title: "AI work orders", text: "Type the complaint; AI drafts cause, correction, labor hours and parts. Estimates, invoices, time clock and inventory included." },
  { icon: Scale, title: "Side-by-side prices", text: "Pick listings and compare price, brand, condition and shipping, then drop the best one straight onto a work order." },
  { icon: Bot, title: "Shop assistant", text: "Ask about open jobs, low stock or this month's revenue in plain words. Cars, heavy trucks, trailers, farm and heavy equipment." },
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/app" : "/login";
  return (
    <div className="min-h-screen">
      <Header />
      <section className="relative overflow-hidden border-b border-border noise">
        <img src={HERO} alt="Mechanic at work" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-36">
          <p className="rise font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6" data-testid="hero-eyebrow">
            // AI parts sourcing for the shop floor
          </p>
          <h1 className="rise text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] max-w-3xl" style={{ animationDelay: ".08s" }}>
            Type the car or the part number.<br />
            <span className="text-primary">We search the whole web.</span>
          </h1>
          <p className="rise mt-8 max-w-xl text-base text-muted-foreground leading-relaxed" style={{ animationDelay: ".16s" }}>
            TorqueSearch sends an AI to dig through retailers, marketplaces and dealer sites, then hands back real listings
            with prices and links. Less tab-hopping, more wrenching.
          </p>
          <div className="rise mt-10 flex flex-wrap gap-4" style={{ animationDelay: ".24s" }}>
            <Button asChild size="lg" className="rounded-sm h-12 px-7 font-semibold hover:bg-orange-600 hover:text-white group" data-testid="hero-cta-btn">
              <Link to={cta}>
                Start a parts search <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <div className="border-b border-border bg-card/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap gap-x-8 gap-y-2 font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <span className="text-primary">Sources →</span>
          {VENDORS.map((v) => <span key={v}>{v}</span>)}
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-5 gap-12 items-center">
        <div className="lg:col-span-3 grid sm:grid-cols-2 gap-px bg-border border border-border">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="bg-background p-8 rise" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`feature-${i}`}>
              <f.icon className="h-6 w-6 text-primary mb-5" />
              <h3 className="text-2xl font-bold uppercase mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 relative">
          <div className="hazard h-2 w-full mb-3" />
          <img src={ENGINE} alt="Engine detail" className="w-full aspect-[4/5] object-cover rounded-sm" />
        </div>
      </section>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground font-mono">
        TorqueSearch — prices come from third-party sites; always confirm fitment before ordering.
      </footer>
    </div>
  );
}
